import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronRight,
  Shield,
  Lock,
  Key,
  Eye,
  EyeOff,
  Server,
  Database,
  UserCheck,
  ShieldCheck,
  CheckCircle,
  Fingerprint,
  Network,
  HardDrive,
  RefreshCw,
  Layers,
  Activity,
  Users,
  Mail,
  Smartphone
} from 'lucide-react';

import ParticleBackground from '../components/ui/ParticleBackground';

const Security = () => {
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] } }
  };

  // Tech grid background
  const TechGrid = () => (
    <div className="absolute inset-0 z-0">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-gray-300 h-full" />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-gray-300 w-full" />
        ))}
      </div>
    </div>
  );

  // Animated scan line
  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/10 to-transparent h-32 z-10"
      animate={{ top: ['-10%', '110%'] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
    />
  );

  // Corner accents
  const CornerAccents = () => (
    <>
      <motion.div 
        className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-gray-700/30"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div 
        className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-gray-700/30"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      />
      <motion.div 
        className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-gray-700/30"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      />
      <motion.div 
        className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-gray-700/30"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
      />
    </>
  );

  // Floating shield elements
  const FloatingShields = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            left: `${10 + i * 12}%`,
            top: `${15 + (i % 4) * 20}%`,
          }}
          animate={{
            y: [0, -15, 0],
            opacity: [0.1, 0.3, 0.1],
            rotate: [0, 10, 0]
          }}
          transition={{
            duration: 5 + i * 0.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.4
          }}
        >
          <Shield className="w-4 h-4 text-gray-600/20" strokeWidth={1} />
        </motion.div>
      ))}
    </div>
  );

  // Hexagon pattern background
  const HexPattern = () => (
    <div className="absolute inset-0 overflow-hidden opacity-5">
      <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
            <polygon 
              points="25,0 50,14.4 50,43.4 25,57.7 0,43.4 0,14.4" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="0.5"
              className="text-gray-500"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hexagons)" />
      </svg>
    </div>
  );

  const securityPillars = [
    {
      title: "Encryption",
      icon: Lock,
      features: [
        { name: "Encryption at rest", desc: "Planned for stored data" },
        { name: "Encryption in transit", desc: "Planned for all network connections" },
        { name: "Secure credential storage", desc: "Planned password hashing with salting" },
        { name: "Encrypted backups", desc: "Planned as part of our backup strategy" }
      ]
    },
    {
      title: "Access Control",
      icon: Key,
      features: [
        { name: "Multi-factor authentication", desc: "Planned for account access" },
        { name: "Role-based permissions", desc: "Planned, scoped by user role" },
        { name: "Session management", desc: "Planned timeout and session handling" },
        { name: "Audit logging", desc: "Planned trail of access and changes" }
      ]
    },
    {
      title: "Infrastructure",
      icon: Server,
      features: [
        { name: "Secure hosting", desc: "Planned, reputable infrastructure providers" },
        { name: "Backups", desc: "Planned automated backup strategy" },
        { name: "Network protections", desc: "Planned firewall and traffic filtering" },
        { name: "Monitoring", desc: "Planned as we scale our systems" }
      ]
    },
    {
      title: "Best Practices",
      icon: ShieldCheck,
      features: [
        { name: "Security reviews", desc: "Planned as part of our development process" },
        { name: "Secure development", desc: "Security-minded coding practices" },
        { name: "Vulnerability management", desc: "Planned scanning and patching process" },
        { name: "Data minimization", desc: "Collecting only what we need" }
      ]
    }
  ];

  const commitments = [
    { icon: Lock, label: "Encryption", desc: "Planned, in progress" },
    { icon: Key, label: "Access Control", desc: "Planned, in progress" },
    { icon: RefreshCw, label: "Backups", desc: "Planned, in progress" },
    { icon: Eye, label: "Monitoring", desc: "Planned, in progress" }
  ];

  const dataProtection = [
    {
      title: "Data at Rest",
      icon: HardDrive,
      items: [
        "Encryption for stored data (planned)",
        "Encrypted backups (planned)",
        "Secure credential storage (planned)",
        "Protected file systems (planned)"
      ]
    },
    {
      title: "Data in Transit",
      icon: Network,
      items: [
        "Encrypted connections (planned)",
        "HTTPS across the site",
        "Secure API communications (planned)",
        "Encrypted email where possible"
      ]
    },
    {
      title: "Data Access",
      icon: Users,
      items: [
        "Role-based access control (planned)",
        "Privilege-aware permissions (planned)",
        "Audit trail logging (planned)",
        "Session management (planned)"
      ]
    }
  ];

  // Decorative divider
  const OrnamentDivider = () => (
    <div className="flex items-center justify-center my-20 px-4">
      <motion.div 
        className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />
      <motion.div 
        className="mx-6 flex items-center gap-3"
        initial={{ opacity: 0, scale: 0.5 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <div className="w-2 h-2 rotate-45 bg-gray-600/40" />
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        >
          <Shield className="w-5 h-5 text-gray-500/50" strokeWidth={1.5} />
        </motion.div>
        <div className="w-2 h-2 rotate-45 bg-gray-600/40" />
      </motion.div>
      <motion.div 
        className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-gray-100">
      {/* Hero Section */}
      <section className="relative min-h-[45vh] flex items-center justify-center overflow-hidden bg-black">
        <ParticleBackground />
        <TechGrid />
        <ScanLine />

        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />

        <div className="container mx-auto px-4 z-20 relative pt-32 pb-20">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gray-700/50 bg-gray-900/50">
                <Shield className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
              </div>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-extralight tracking-wider text-white mb-3"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Security
            </motion.h1>

            <motion.p variants={itemVariants} className="text-gray-400 tracking-widest text-sm mb-6">
              RETICULI TECHNOLOGIES
            </motion.p>

            <motion.p
              variants={itemVariants}
              className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed"
            >
              As an early-stage company, we're still building toward client engagements. These are
              the security commitments we're implementing as we grow.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Security Commitments Cards */}
      <section className="relative py-12 bg-gradient-to-b from-black to-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {commitments.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative bg-gray-900/60 border border-gray-700/30 rounded-lg p-5 text-center hover:border-gray-600/50 transition-colors duration-300"
                >
                  <item.icon className="w-8 h-8 mx-auto mb-3 text-gray-400" strokeWidth={1.5} />
                  <h3 className="text-sm font-medium text-white mb-1">{item.label}</h3>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Pillars Section */}
      <section className="relative py-24 overflow-hidden">
        <TechGrid />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-4"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Security Framework
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Multiple layers of protection safeguarding your organization's data
            </p>
          </motion.div>

          {/* Pillar Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {securityPillars.map((pillar, index) => (
              <motion.button
                key={index}
                onClick={() => setActiveSection(index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-5 py-3 rounded-lg transition-all duration-300 flex items-center gap-2 text-sm tracking-wider ${
                  activeSection === index 
                    ? 'bg-gray-800 text-white border border-gray-600/50 shadow-lg' 
                    : 'text-gray-500 hover:text-gray-300 border border-transparent hover:border-gray-800'
                }`}
              >
                <pillar.icon size={16} className={activeSection === index ? 'text-gray-300' : ''} />
                {pillar.title}
              </motion.button>
            ))}
          </div>

          {/* Active Pillar Display */}
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-br from-gray-900/60 to-gray-900/30 border border-gray-800/50 rounded-lg p-8 md:p-10">
              <div className="flex items-center gap-4 mb-8">
                <motion.div 
                  className="w-14 h-14 rounded-lg bg-gray-800/50 border border-gray-700/30 flex items-center justify-center"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                >
                  {React.createElement(securityPillars[activeSection].icon, {
                    size: 28,
                    className: 'text-gray-300',
                    strokeWidth: 1.5
                  })}
                </motion.div>
                <div>
                  <h3 
                    className="text-2xl font-light tracking-wider text-white"
                    style={{ fontFamily: 'Georgia, Cambria, serif' }}
                  >
                    {securityPillars[activeSection].title}
                  </h3>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {securityPillars[activeSection].features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                    className="flex items-start gap-3 group p-3 rounded-lg hover:bg-gray-800/30 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5 text-gray-500 group-hover:text-gray-400 transition-colors mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-gray-200 block">{feature.name}</span>
                      <span className="text-xs text-gray-500">{feature.desc}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <OrnamentDivider />

      {/* Data Protection Section */}
      <section className="relative py-24">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-4"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Data Protection
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Security at every stage of the data lifecycle
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {dataProtection.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -5 }}
                className="p-8 bg-gradient-to-br from-gray-900/50 to-gray-900/20 border border-gray-800/50 rounded-lg hover:border-gray-700/50 transition-all duration-300"
              >
                <motion.div 
                  className="w-14 h-14 mb-6 rounded-full bg-gray-800/50 border border-gray-700/30 flex items-center justify-center"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <section.icon className="w-7 h-7 text-gray-400" strokeWidth={1.5} />
                </motion.div>
                <h3 
                  className="text-xl font-medium text-white mb-4"
                  style={{ fontFamily: 'Georgia, Cambria, serif' }}
                >
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <OrnamentDivider />

      {/* Trust Section */}
      <section className="relative py-24 overflow-hidden">
        <ScanLine />
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-6"
                style={{ fontFamily: 'Georgia, Cambria, serif' }}
              >
                Built on Trust
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Trust is the foundation of every partnership. As an early-stage company, we're
                  building our security practices deliberately, rather than claiming more than
                  we've implemented.
                </p>
                <p>
                  We take data protection seriously even while we're small, and we'll publish
                  specifics here as our security program matures.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { icon: UserCheck, label: "Account Security", desc: "Multi-factor authentication (planned)" },
                { icon: EyeOff, label: "Privacy Focused", desc: "Data minimization practices" },
                { icon: RefreshCw, label: "Backups", desc: "Automated backups (planned)" },
                { icon: Activity, label: "Monitoring", desc: "System health tracking (planned)" },
                { icon: Database, label: "Secure Storage", desc: "Encrypted databases (planned)" },
                { icon: Layers, label: "Layered Security", desc: "Multiple security layers (planned)" }
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                  className="p-5 bg-gray-900/40 border border-gray-800/30 rounded-lg hover:border-gray-700/50 transition-all duration-300"
                >
                  <item.icon className="w-6 h-6 text-gray-500 mb-3" strokeWidth={1.5} />
                  <h4 className="text-sm font-medium text-white mb-1">{item.label}</h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* User Privacy Section */}
      <section className="relative py-24 bg-gradient-to-b from-transparent via-gray-900/20 to-transparent">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-extralight tracking-wider text-white mb-4"
                style={{ fontFamily: 'Georgia, Cambria, serif' }}
              >
                User Privacy
              </h2>
              <p className="text-gray-500">
                Your personal information is handled with care
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Lock,
                  title: "Secure Credentials",
                  desc: "We plan to store passwords using industry-standard hashing, never in plain text."
                },
                {
                  icon: Fingerprint,
                  title: "Personal Data Protection",
                  desc: "We plan to limit access to personal information to authorized personnel only."
                },
                {
                  icon: Mail,
                  title: "Communication Security",
                  desc: "We aim to use secure channels for sensitive communications as our tooling matures."
                },
                {
                  icon: Smartphone,
                  title: "Secure Access",
                  desc: "We use HTTPS to help protect data transmitted between your device and our site."
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 bg-gray-900/40 border border-gray-800/30 rounded-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-800/50 border border-gray-700/30 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-gray-400" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-white mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <ParticleBackground />
        <TechGrid />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/20 to-transparent" />
        <CornerAccents />
        
        <div className="container mx-auto px-6 relative z-20">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div 
              className="inline-flex items-center justify-center w-20 h-20 mb-8 rounded-full border border-gray-700/50 bg-gray-900/50"
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            >
              <Shield className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
            </motion.div>
            
            <h2 
              className="text-4xl md:text-5xl font-extralight tracking-wider text-white mb-6"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Questions About Security?
            </h2>
            <p className="text-gray-500 mb-12 text-lg">
              We're committed to protecting your organization's data. Contact us to learn more about 
              our security practices or to discuss your specific requirements.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to="/about"
                  className="group px-12 py-5 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-medium tracking-wider rounded transition-all duration-300 flex items-center justify-center shadow-xl shadow-black/30 border border-gray-600/30"
                >
                  About Us
                  <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={20} />
                </Link>
              </motion.div>
              <Link
                to="/contact"
                className="px-12 py-5 border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white font-medium tracking-wider rounded transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer accent */}
      <div className="h-px bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />
    </div>
  );
};

export default Security;